import { h, Component, Host, Element, Prop, Event, EventEmitter, Watch, State, Method, Listen } from "@stencil/core";
import pfx from "../../../../../../utils/style-class-prefix-helper";
import {
  LocalizableComponent,
  setCurrentLocalizationDelegate,
  Localization
} from "../../../../../../common/localizable-component";
import { computeLinkAriaLabel } from "../../../../../../utils/link-helper";
import { NAVIGATION_MAIN_ICON_LINK_TARGET_SELF } from "./constants/navigation-main-icon-link-constants";
import { NAVIGATION_MAIN_ICON_LINK_LOCALIZATION } from "./constants/navigation-main-icon-link-i18n";
import {
  NavigationMainIconLinkTarget,
  NavigationMainIconLinkLocalization
} from "./constants/navigation-main-icon-link-types";
import { KeyIds } from "../../../../../../constants/keys";
import { isTabletOrSmaller } from "../../../../../../utils/screen-helper";
// import { InitLogging } from "../../../../../../decorators/init-logging";

/**
 * @slot context-menu - holds eon-ui-context-menu component
 */
@Component({
  tag: "eon-ui-navigation-main-icon-link",
  styleUrl: "navigation-main-icon-link.scss",
  shadow: true
})
export class NavigationMainIconLink implements LocalizableComponent<NavigationMainIconLinkLocalization> {
  // @InitLogging() sentryTransaction;
  private currentAriaLabel: string;

  localization: Localization<NavigationMainIconLinkLocalization> = NAVIGATION_MAIN_ICON_LINK_LOCALIZATION;
  currentLocalization: NavigationMainIconLinkLocalization;
  navigation: HTMLEonUiNavigationElement;
  contextMenu: HTMLEonUiContextMenuElement;

  @Element() hostElement: HTMLEonUiNavigationMainIconLinkElement;

  @State() active: boolean = false;
  @State() isTabletOrSmaller: boolean;

  /**
   * Raised when the link has been clicked.
   */
  @Event() navigationMainIconLinkClick: EventEmitter;

  /**
   * Raised when the link has been loaded.
   */
  @Event() navigationMainIconLinkLoad: EventEmitter;

  /**
   * Defines the text shown as label for the link.
   */
  @Prop() text: string;

  /**
   * Set icon for the link
   * @example "phone".
   */
  @Prop() icon: string;

  /**
   * Defines the target url of a Link.
   * Make sure to prefix this with a protocol if the link should display an icon automatically.
   * Valid protocols are "http://", "https://", "mailto:" and "tel:".
   * @example http://www.google.de, tel:0123456789
   */
  @Prop({ mutable: true }) href: string;

  /**
   * Specifies how the link is opened in the browser, e.g. in new window/tab.
   * @example "_blank", "_self", "_parent", "_top"
   * @default "_self"
   */
  @Prop() target: NavigationMainIconLinkTarget = NAVIGATION_MAIN_ICON_LINK_TARGET_SELF;

  /**
   * Specifies the label to use for accessibility.
   * Always provide this for pure icon buttons!
   */
  @Prop() ariaLabel: string;

  /**
   * Set link to current if it matches the current url.
   */
  @Prop() current: boolean;

  /**
   * Enter ID of main sub navigation to be opened.
   */
  @Prop() subNavRel: string;

  /**
   * Removes active state
   */
  @Method()
  async removeActiveState() {
    this.active = false;
  }

  @Listen("resize", { target: "window" })
  onWindowresize() {
    this.isTabletOrSmaller = isTabletOrSmaller();
  }

  componentWillLoad() {
    this.setCurrentLocalization();
    this.computeAriaLabel();
    this.onWindowresize();
    this.getRelatedComponents();
    this.initContextMenu();
  }

  componentDidLoad() {
    this.navigationMainIconLinkLoad.emit();
  }

  @Watch("href")
  private computeAriaLabel() {
    if (this.ariaLabel || !this.currentLocalization) {
      this.currentAriaLabel = this.ariaLabel;
      return;
    }
    this.currentAriaLabel = computeLinkAriaLabel(this.currentLocalization, this.href, false);
  }

  getRelatedComponents() {
    this.navigation = document.getElementById("eonui-navigation") as HTMLEonUiNavigationElement;
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  async handleClick(_event: Event) {
    this.navigationMainIconLinkClick.emit(this.href);

    if (!!this.subNavRel) {
      _event.preventDefault();

      if (!!this.isTabletOrSmaller) {
        if (this.active) {
          this.navigation.closeMobileNav();
          this.active = false;
        } else {
          await this.navigation.openMobileSubImmediately(this.subNavRel);
          this.active = true;
        }
      } else {
        if (this.active) {
          this.navigation.closeMainSub();
          this.active = false;
        } else {
          await this.navigation.openMainSub(this.subNavRel);
          this.active = true;
        }
      }
    }

    if (this.contextMenu) {
      _event.preventDefault();
      this.contextMenu.visible = true;
    }
  }

  async handleKeyDown(_event: KeyboardEvent) {
    this.navigationMainIconLinkClick.emit();

    if (!!this.subNavRel) {
      if (_event.key === KeyIds.ENTER || _event.key === KeyIds.SPACE) {
        _event.preventDefault();

        if (this.active) {
          this.navigation.closeMainSub();
          this.active = false;
        } else {
          await this.navigation.openMainSub(this.subNavRel, true);
          this.active = true;
        }
      }
    }

    if (this.contextMenu) {
      if (_event.key === KeyIds.ENTER || _event.key === KeyIds.SPACE) {
        this.contextMenu.visible = true;
      }
    }
  }

  /**
   * Context Menu Setup including getter, click events, keyboard events and properties
   */
  private initContextMenu() {
    this.contextMenu = this.hostElement.querySelector('[slot="context-menu"]');
  }

  render() {
    return (
      <Host>
        <a
          title={this.hostElement.getAttribute("title")}
          href={this.href}
          target={this.target}
          class={pfx({ current: !!this.current, active: !!this.active })}
          aria-label={this.currentAriaLabel}
          onClick={(event: MouseEvent) => this.handleClick(event)}
          role="menuitem"
          tabindex="0"
          onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
        >
          {this.icon && (
            <span class={pfx("icon-wrapper")}>
              <eon-ui-icon class={pfx("icon")} name={this.icon} no-space="false" aria-hidden="true"></eon-ui-icon>
            </span>
          )}
          {this.text && (
            <span class={pfx("text-wrapper")}>
              <span class={pfx("text")} innerHTML={this.text} />
            </span>
          )}
        </a>
        <slot name="context-menu" />
      </Host>
    );
  }
}
